// Boot READ-priority gate for the Capacitor SQLite adapter (S3, #3986 Step 19).
//
// Problem: on iOS native, the initial sync writes documents in chunked
// batches over the ONE native SQLite bridge. Reads (`_get`, `_allDocs`) do
// not go through the existing `WritePriorityGate` at all — that gate only
// arbitrates user-writes vs sync-writes. So during a cold boot the awaited,
// splash-blocking boot reads (singletons, plan, schedule, view queries)
// queue up behind sync-write batches and the boot gets slower the bigger
// the account is.
//
// This gate gives READS priority over sync writes, but ONLY while a boot is
// in progress:
// - The adapter's read paths bracket themselves with `enterRead`/`exitRead`
//   (always — the counter is cheap).
// - The sync chunk loop calls `waitForPendingBootReads()` between chunks.
//   It waits only when BOTH a boot is in progress AND reads are pending;
//   otherwise it resolves immediately, so post-boot behavior is unchanged.
// - The boot window is controlled from outside the adapter via
//   `setBootReadPriorityActive(true/false)` — the app boot sequence turns it
//   on at boot start and off when the boot reaches Ready. The adapter has no
//   dependency injection, so this module-level switch is the channel in
//   (same reasoning as the callback-only error surface documented for the
//   L11 open timeout).
// - FAILSAFE: a boot that never reaches Ready (fatal error, abandoned boot)
//   must not leave sync throttled forever. Activating the gate arms a
//   one-shot timer that force-deactivates it after
//   BOOT_READ_PRIORITY_FAILSAFE_MS. An explicit deactivate clears the timer.
//
// Starvation note: while the gate is active, a continuous stream of reads
// can hold sync writes off indefinitely. That is the intended trade during
// the boot window (boot reads ARE the critical path), and the window is
// bounded by Ready + the failsafe, so sync can never be starved for longer
// than the failsafe.
//
// Scope boundary (deliberate): only `_get` and `_allDocs` count as reads —
// they carry the splash-blocking boot reads (singletons, plan, schedule,
// and the view queries, which read through `_allDocs` on the index DB).
// `_getLocal`, `_info`, `_getRevisionTree`, and the `_changes` feed are NOT
// counted: they are sync/housekeeping traffic, and counting `_changes`
// would let the sync engine's own reads hold off its writes.
//
// Pure module: no Angular, no Capacitor imports; timers are injectable so
// the unit test (boot-read-priority.test.ts) drives them by hand.

// how long the gate may stay active without an explicit deactivate. Chosen
// to comfortably cover the slowest real boots (cold-native p90 was ~62 s)
// while still bounding a wedged boot's impact on sync.
export const BOOT_READ_PRIORITY_FAILSAFE_MS = 180_000;

type TimerHandle = ReturnType<typeof setTimeout>;

export class BootReadPriorityGate {
  private pendingReads = 0;
  private resolvers: (() => void)[] = [];
  private bootActive = false;
  private failsafeHandle: TimerHandle | null = null;
  private onFailsafeFired: (() => void) | null = null;

  // the default timers wrap the globals in arrow functions on purpose. A bare
  // `= setTimeout` default stores the native reference, and the shared
  // singleton then calls it as `this.setTimeoutFn(...)`, which invokes the
  // native timer with `this` set to the gate instance. In a browser that
  // throws `TypeError: Illegal invocation`, because `window.setTimeout`
  // requires its receiver to be the window — the crash fired on every boot and
  // took down `appBootSequenceAsync`. The arrow wrappers call the globals as
  // plain (unbound) calls, so the receiver is always correct. The unit test
  // injects its own fake timers, so it never exercised this path.
  constructor(
    private readonly setTimeoutFn: (cb: () => void, ms: number) => TimerHandle = (cb, ms) => setTimeout(cb, ms),
    private readonly clearTimeoutFn: (handle: TimerHandle) => void = handle => clearTimeout(handle),
  ) {}

  // turn the boot window on/off. Activating (re)arms the failsafe timer;
  // deactivating clears it and releases any waiting sync chunk.
  // `onFailsafeFired` (activation only) surfaces the abnormal case where the
  // FAILSAFE — not an explicit close — ended the window: that means the boot
  // never reached Ready while sync stayed read-prioritized for the full
  // window. The module is deliberately pure (no Sentry import), so the
  // caller injects the reporter; an explicit close never invokes it.
  setBootActive(active: boolean, onFailsafeFired?: () => void): void {
    // always clear a previously armed failsafe first, so repeated activates
    // re-arm cleanly and a deactivate never leaves a stale timer
    if (this.failsafeHandle !== null) {
      this.clearTimeoutFn(this.failsafeHandle);
      this.failsafeHandle = null;
    }
    this.bootActive = active;
    if (active) {
      this.onFailsafeFired = onFailsafeFired ?? null;
      this.failsafeHandle = this.setTimeoutFn(() => {
        this.failsafeHandle = null;
        // surface before closing: a fired failsafe is a wedged/abandoned
        // boot, and the sibling watchdogs (SqliteOpenTimeout,
        // SyncMutexHeldTooLong, ConsentGateSlow) all report their backstops
        this.onFailsafeFired?.();
        this.setBootActive(false);
      }, BOOT_READ_PRIORITY_FAILSAFE_MS);
    } else {
      this.onFailsafeFired = null;
      // if the window closes, sync must not stay parked
      this.releaseWaiters();
    }
  }

  // true while the boot window is open (exposed for tests/diagnostics)
  isBootActive(): boolean {
    return this.bootActive;
  }

  // read paths call these unconditionally; the counter is cheap and keeping
  // it always-on avoids enter/exit imbalance when the boot flag flips while
  // a read is in flight
  enterRead(): void {
    this.pendingReads++;
  }

  exitRead(): void {
    // guard against an unbalanced exit (a bug upstream, not a real state) —
    // going negative would make waitForPendingBootReads wait forever. This
    // is a CONSCIOUS silent absorb: the read paths bracket enter/exit in
    // try/finally so an imbalance is effectively impossible, and the pure
    // module has no reporter — "cannot go negative and hang sync" wins over
    // surfacing a can't-happen bug.
    if (this.pendingReads > 0) {
      this.pendingReads--;
    }
    if (this.pendingReads === 0) {
      this.releaseWaiters();
    }
  }

  // called by the sync chunk loop between chunks. Resolves immediately
  // outside the boot window or when no reads are pending.
  async waitForPendingBootReads(): Promise<void> {
    if (!this.bootActive || this.pendingReads === 0) {
      return;
    }
    return new Promise(resolve => this.resolvers.push(resolve));
  }

  // resolve every parked sync chunk (reads drained or window closed)
  private releaseWaiters(): void {
    const fns = this.resolvers.splice(0);
    for (const fn of fns) {
      fn();
    }
  }
}

// the one shared gate instance. Shared across all adapter instances on
// purpose: "boot in progress" is a property of the app, not of one database.
export const bootReadPriorityGate = new BootReadPriorityGate();

// the switch the app boot sequence flips (see module comment). Exported as a
// plain function so callers outside the adapter do not need the class.
// `onFailsafeFired` is only meaningful when activating — the boot sequence
// passes a Sentry reporter so a fired failsafe (wedged boot) is visible.
export function setBootReadPriorityActive(active: boolean, onFailsafeFired?: () => void): void {
  bootReadPriorityGate.setBootActive(active, onFailsafeFired);
}
